import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {
    SimplePresentation,
    SimplePresentationRunner,
    SimplePresentationRunnerController
} from "../../core/winpresentation/SimplePresentationRunner";
import {xBomb} from "../runnerpresentations/XBomb";
import {xNudge} from "../runnerpresentations/xNudge";
import {CombinedWin} from "../runnerpresentations/CombinedWin";
import {xWaysReveal} from "../runnerpresentations/xWaysReveal";
import {ShowEndScreen} from "../runnerpresentations/ShowEndScreen";
import {DasUBoot} from "../DasUBoot";
import {FreespinReTriggerPresentation} from "../runnerpresentations/FreespinReTriggerPresentation";
import {LocalPresentation} from "../runnerpresentations/LocalPresentation";
import {DasUBootGameMode} from "../server/DasUBootGameMode";
import {DasUBootRoundType} from "../server/DasUBootData";
import {gsap} from "gsap";
import {SymbolUtils} from "../utils/SymbolUtils";
import {DasUBootSymbol, DasUBootSymbolStates} from "../reels/DasUBootSymbol";
import {TorpedoPartsPresentation} from "../runnerpresentations/TorpedoPartsPresentation";
import {TorpedoLaunch} from "../runnerpresentations/TorpedoLaunch";
import {XBombMultiplierUpdatePresentation} from "../runnerpresentations/XBombMultiplierUpdatePresentation";
import {MissedWin} from "../runnerpresentations/MissedWin";
import {isStackedWildSymbol} from "../reels/symbols/StackedWildSymbol";
import {SimpleReelModelSymbolData} from "../../core/reel/simplereel/SimpleReelModel";
import {IndiSymWinPresentation} from "../runnerpresentations/IndiSymWinPresentation";


export class NormalWinPresentation implements FlowStatePresentation {
    private _presentations:SimplePresentation[];

    private shouldExitFSMode():boolean {
        return DasUBoot.model.data.roundType == DasUBootRoundType.FREE_SPIN && DasUBoot.model.data.nextMode == DasUBootGameMode.NORMAL;
    }

    private shouldExitSFSMode():boolean {
        return DasUBoot.model.data.roundType == DasUBootRoundType.SUPER_FREE_SPIN && DasUBoot.model.data.nextMode == DasUBootGameMode.NORMAL;
    }

    private shouldShowExitScreen():boolean {
        return DasUBoot.model.winCapReached || this.shouldExitFSMode() || this.shouldExitSFSMode();
    }

    private shouldAddNewTorpedo():boolean {
        return DasUBoot.model.data.roundType == DasUBootRoundType.SUPER_FREE_SPIN &&
            DasUBoot.model.data.nextMode != DasUBootGameMode.AVALANCHE &&
            !!DasUBoot.model.gameData.torpedo?.isComplete;
    }


    constructor() {
        this._presentations = [
            new xWaysReveal(),
            new xNudge(),
            new XBombMultiplierUpdatePresentation(),
            new TorpedoPartsPresentation(),
            new xBomb(),
            new TorpedoLaunch(),
            new LocalPresentation((controller:SimplePresentationRunnerController) => this.splitXNudge(controller), ()=>true),
            new IndiSymWinPresentation(),
            new CombinedWin(),
            new MissedWin(),
            new FreespinReTriggerPresentation(),

            new LocalPresentation((controller:SimplePresentationRunnerController) => this.generalFreespinReset(controller), () => this.shouldShowExitScreen()),
            new LocalPresentation((controller:SimplePresentationRunnerController) => this.resetFreeSpinMode(controller), () => this.shouldExitFSMode()),
            new LocalPresentation((controller:SimplePresentationRunnerController) => this.addNewTorpedo(controller), () => this.shouldAddNewTorpedo()),
            new LocalPresentation((controller:SimplePresentationRunnerController) => this.resetSuperFreeSpinMode(controller), () => this.shouldExitSFSMode()),


            new ShowEndScreen(() => this.shouldShowExitScreen())
        ];
    }

    public start(flowStateController:FlowStateController) {
        if (DasUBoot.model.winCapReached){
            const presentations = [
                new xWaysReveal(),
                new xNudge(),
                new XBombMultiplierUpdatePresentation(),
                new TorpedoPartsPresentation(),
                new xBomb(),
                new TorpedoLaunch(),
                new LocalPresentation((controller:SimplePresentationRunnerController) => this.splitXNudge(controller), ()=>true),
                new CombinedWin(),

                new LocalPresentation((controller:SimplePresentationRunnerController) => this.generalFreespinReset(controller), () => this.shouldShowExitScreen()),

                new ShowEndScreen(() => this.shouldShowExitScreen()),

                new LocalPresentation((controller:SimplePresentationRunnerController) => this.resetFreeSpinMode(controller), () => this.shouldExitFSMode()),
                new LocalPresentation((controller:SimplePresentationRunnerController) => this.resetSuperFreeSpinMode(controller), () => this.shouldExitSFSMode()),
                new LocalPresentation((controller:SimplePresentationRunnerController) => this.reInitReels(controller), () => this.shouldExitSFSMode()),


                new LocalPresentation((controller:SimplePresentationRunnerController) => {
                    const tl = gsap.timeline({onComplete:() => controller.finish()});
                    tl.add( DasUBoot.reelView.reelFrame.hideCover());
                }, ()=>{return true}),
            ];
            SimplePresentationRunner.run(presentations, () => flowStateController.finish(), (callback?:Function) => flowStateController.setSkipCallback(callback));
        } else {
            SimplePresentationRunner.run(this._presentations, () => flowStateController.finish(), (callback?:Function) => flowStateController.setSkipCallback(callback));
        }
    }

    private resetFreeSpinMode(controller:SimplePresentationRunnerController) {
        const tl = gsap.timeline({onComplete:() => controller.finish()});
        tl.add([
            DasUBoot.reelView.raisePeriscope(false),
            DasUBoot.reelView.reelFrame.xBombMultiplierDisplay.setMultiplier(0),
        ], 0.5);
    }

    private addNewTorpedo(controller:SimplePresentationRunnerController) {
        const tl = gsap.timeline({onComplete:() => controller.finish()});
        tl.add(DasUBoot.reelView.reelFrame.torpedoView.clearTorpedo(true));
        tl.add(DasUBoot.reelView.reelFrame.animateXLamps(true));
    }

    private resetSuperFreeSpinMode(controller:SimplePresentationRunnerController) {
        const tl = gsap.timeline({onComplete:() => controller.finish()});
        tl.add([
            DasUBoot.reelView.reelFrame.animateXLamps(true),
            DasUBoot.reelView.reelFrame.showCover(0.2),
            DasUBoot.reelView.reelFrame.torpedoView.clearTorpedo(),
        ]);

        tl.add([
            DasUBoot.reelView.reelFrame.lowerTorpedoTube(false),
            DasUBoot.reelView.reelFrame.xBombMultiplierDisplay.setMultiplier(0),
        ], 0.5);

    }

    private reInitReels(controller:SimplePresentationRunnerController) {
        DasUBoot.reelView.reInitReels(DasUBoot.model.reelsEnteringFreespin);
        controller.finish();
    }


    private generalFreespinReset(controller:SimplePresentationRunnerController):void {
        const tl = gsap.timeline({onComplete:() => controller.finish()});
        tl.add(()=>{
            DasUBoot.reelView.reelFrame.switchLampOnReel(0, -1);
            DasUBoot.reelView.reelFrame.switchLampOnReel(1, -1);
            DasUBoot.reelView.reelFrame.switchLampOnReel(2, -1);
            DasUBoot.reelView.reelFrame.switchLampOnReel(3, -1);
            DasUBoot.reelView.reelFrame.switchLampOnReel(4, -1);
            DasUBoot.reelView.reelFrame.switchLampOnReel(5, -1);
            
            DasUBoot.keypadPlugin.setZeroBetSpinCounter(-1);

            const allVisibleSymbols = DasUBoot.reelView.getAllVisibleSymbols();
            SymbolUtils.getSymbolStateAnimations(allVisibleSymbols, DasUBootSymbolStates.NORMAL);
            for (const sym of allVisibleSymbols) {
                (<DasUBootSymbol>sym.positionable).moveToNormalLayer();
            }
        });
        tl.add(gsap.to(this, {duration:0.001}));
    }


    private splitXNudge(controller:SimplePresentationRunnerController) {
        const tl:gsap.core.Timeline = gsap.timeline({onComplete:()=>controller.finish()});

        const winSymbols:SimpleReelModelSymbolData[][] = SymbolUtils.getAllSymbolsWithStateByReel(DasUBootSymbolStates.WIN);

        for (let reel of winSymbols){
            for (let symbol of reel){
                if (isStackedWildSymbol(symbol.positionable)){
                    const stackedSymbol = symbol.positionable;
                    const splitAnimation = stackedSymbol.nudgeComplete();
                    splitAnimation.add(() => {DasUBoot.reelView.reels[symbol.reelId].replaceVisibleSymbol(symbol.reelPosition, "W", false)}, 0);
                    splitAnimation.add(() => stackedSymbol.destroy());
                    tl.add(splitAnimation)

                }
            }
        }

        tl.add(gsap.to(this, {duration:0.0001}));


    }


}